import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type {
  ScopedFeatureGuards,
  ApiResponse,
  AccountsListResponse,
  AccountDetail,
  UpdateAccountDto,
  FeatureGuards,
  AiInsights,
  Products,
  ActivityEvent,
  Pagination,
  AccountsFilters
} from '@/types';

export interface AccountsQuery extends Partial<AccountsFilters> {
  page?: number;
  pageSize?: number;
}

export const accountService = {
  async list(query: AccountsQuery): Promise<AccountsListResponse> {
    const params: Record<string, string | number> = {
      page: query.page ?? 1,
      pageSize: query.pageSize ?? 25,
      source: query.source ?? 'all'
    };
    if (query.search) params.q = query.search;
    if (query.account_status && query.account_status !== 'all') {
      params.account_status = query.account_status;
    }
    if (query.shadow_mode_enabled && query.shadow_mode_enabled !== 'all') {
      params.shadow_mode_enabled = query.shadow_mode_enabled;
    }
    if (query.v3 && query.v3 !== 'all') params.v3 = query.v3;

    const res = await api.get<ApiResponse<AccountsListResponse>>(
      API_ENDPOINTS.ACCOUNTS.BASE,
      { params }
    );
    return res.data.data;
  },

  async get(id: number): Promise<AccountDetail> {
    const res = await api.get<ApiResponse<AccountDetail>>(
      API_ENDPOINTS.ACCOUNTS.BY_ID(id)
    );
    return res.data.data;
  },

  async update(id: number, payload: UpdateAccountDto): Promise<UpdateAccountDto> {
    const res = await api.patch<ApiResponse<UpdateAccountDto>>(
      API_ENDPOINTS.ACCOUNTS.BY_ID(id),
      payload
    );
    return res.data.data;
  },

  /**
   * The guards as ONE SCOPE reads them: a brand's own values over the account's, or the
   * account's alone when no brand is named.
   */
  async getFeatureGuards(id: number, buId: number | null): Promise<ScopedFeatureGuards> {
    const res = await api.get<ApiResponse<ScopedFeatureGuards>>(
      API_ENDPOINTS.ACCOUNTS.FEATURE_GUARDS(id),
      buId == null ? {} : { params: { bu_id: buId } }
    );
    return res.data.data;
  },

  /**
   * `buId` scopes the write. With one, the brand takes OWNERSHIP of every key in the patch,
   * changed or not: a toggle that saved nothing would leave the brand tracking the account
   * and flip the next time someone changed it there.
   */
  async updateFeatureGuards(
    id: number,
    patch: Partial<FeatureGuards>,
    buId: number | null = null
  ): Promise<ScopedFeatureGuards & { changed: string[] }> {
    const res = await api.patch<ApiResponse<ScopedFeatureGuards & { changed: string[] }>>(
      API_ENDPOINTS.ACCOUNTS.FEATURE_GUARDS(id),
      buId == null ? patch : { ...patch, bu_id: buId }
    );
    return res.data.data;
  },

  /** Hand a brand's guards back to the account so it inherits again. */
  async resetFeatureGuards(
    id: number,
    buId: number,
    keys?: string[]
  ): Promise<ScopedFeatureGuards & { cleared: string[] }> {
    const res = await api.delete<ApiResponse<ScopedFeatureGuards & { cleared: string[] }>>(
      API_ENDPOINTS.ACCOUNTS.FEATURE_GUARDS(id),
      { data: { bu_id: buId, ...(keys?.length ? { keys } : {}) } }
    );
    return res.data.data;
  },

  async updateAiInsights(
    id: number,
    patch: Partial<AiInsights>
  ): Promise<{ aiInsights: AiInsights; changed: string[] }> {
    const res = await api.patch<
      ApiResponse<{ aiInsights: AiInsights; changed: string[] }>
    >(API_ENDPOINTS.ACCOUNTS.AI_INSIGHTS(id), patch);
    return res.data.data;
  },

  async updateProduct(
    id: number,
    product: string,
    patch: { enabled?: boolean; max_ticket_fetch?: number }
  ): Promise<{ products: Products }> {
    const res = await api.patch<ApiResponse<{ products: Products }>>(
      API_ENDPOINTS.ACCOUNTS.PRODUCT(id, product),
      patch
    );
    return res.data.data;
  },

  async activity(
    id: number,
    page = 1,
    pageSize = 25
  ): Promise<{ events: ActivityEvent[]; pagination: Pagination }> {
    const res = await api.get<
      ApiResponse<{ events: ActivityEvent[]; pagination: Pagination }>
    >(API_ENDPOINTS.ACCOUNTS.ACTIVITY(id), { params: { page, pageSize } });
    return res.data.data;
  }
};
